import React, { useState, useEffect, useRef } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import { toast } from 'react-toastify';
import { config } from '../config/config';
import { analytics } from '../firebase/firebase';
import { logEvent } from 'firebase/analytics';

const GoogleAuth = ({ onLoginSuccess, id }, ref) => {
  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState(null);

  const login = useGoogleLogin({
    onSuccess: async (response) => {
      setIsLoading(false);
      if (response?.access_token) {
        await handleGoogleLogin(response.access_token);
      } else {
        toast.error('Google login failed. Please try again.');
      }
    },
    onError: () => {
      setIsLoading(false);
      toast.error('Login failed. Please try again.');
    },
    scope: 'openid profile email',
    responseType: 'token',
  });

  const handleButtonClick = () => {
    if (!isLoading) {
      setIsLoading(true);
         logEvent(analytics, 'login_button_click', {
      button_id: id || 'unknown',
    });
      login();
    }
  };

  const handleGoogleLogin = async (googleToken) => {
    try {
      const profileRes = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
        headers: {
          Authorization: `Bearer ${googleToken}`,
        },
      });

      if (!profileRes.ok) {
        toast.error('Failed to fetch user profile from Google.');
        return;
      }

      const profile = await profileRes.json();

      // Send profile to backend (backend will set HTTP-only cookie)
      const backendRes = await fetch(`${config.baseURL}/login.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // ✅ ensures cookie is set
        body: JSON.stringify({
          email: profile.email,
          name: profile.name,
          social_id: profile.id,
          profile_picture: profile.picture,
          is_email_verified: profile.verified_email,
        }),
      });

      const backendData = await backendRes.json();

      if (backendData?.user_id) {
        const userData = {
          email: profile.email,
          name: profile.name,
          picture: profile.picture,
          id: backendData.user_id,
        };

        setUser(userData);
        onLoginSuccess?.(userData);

        logEvent(analytics, 'login', { method: 'Google', email: profile.email });
      } else {
        toast.error('Authentication failed. Please try again.');
      }
    } catch (error) {
      console.error(error);
      toast.error('An error occurred during login.');
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${config.baseURL}/logout.php`, {
        method: 'POST',
        credentials: 'include', // let backend clear the cookie
      });

      logEvent(analytics, 'logout', { method: 'Google' });
      setUser(null);
      localStorage.clear();
      sessionStorage.clear();
      window.location.reload();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  React.useImperativeHandle(ref, () => ({
    login: handleButtonClick,
  }));


  useEffect(() => {
  const checkLoginStatus = async () => {
    try {
      const response = await fetch(
        `${config.baseURL}/validate_token.php`,
        {
          method: "GET",
          credentials: "include",
        }
      );

      const data = await response.json();

      if (data?.user) {
        setUser(data.user);
        
        onLoginSuccess?.(data.user);
      } else {

        sessionStorage.removeItem("historyData");
        sessionStorage.removeItem("hasFetchedHistory");
      }
    } catch {
      // 🔥 NETWORK ERROR → CLEAR HISTORY
      setUser(null);
      sessionStorage.removeItem("historyData");
      sessionStorage.removeItem("hasFetchedHistory");
    }
  };

  checkLoginStatus();
}, []);


  return (
    <>
      {user ? (
        <button onClick={handleLogout} id={id}>Logout</button>
      ) : (
        <button onClick={handleButtonClick} id={id} disabled={isLoading}>
          {isLoading ? 'Logging in...' : 'Login'}
        </button>
      )}
    </>
  );
};

export default React.forwardRef(GoogleAuth);
